// app/src/lib/sam/root.js — root application state (minimal observable store).
//
// HISTORY: this previously imported `{ create }` from @cognitive-fab/sam-pattern
// and @cognitive-fab/sam-fsm and configured them XState-style. Neither package
// exports `create` (sam-pattern → `createInstance`, sam-fsm → `fsm`), and the
// config shape / `fsm.next()` calls don't match the real sam-fsm API — so the
// module threw at import time, which took down the ENTIRE kanjo-lib bundle and
// left the PWA stuck on its splash screen.
//
// Nothing in the app consumes this root yet (it's only re-exported as
// `kanjo.sam`). It's replaced here with a small, dependency-free observable
// store exposing the same surface (dispatch / subscribe / getModel). When state
// management is actually adopted, re-implement this against the real
// sam-pattern (createInstance + acceptors/reactors/NAPs) and sam-fsm (fsm({...}))
// APIs.

// Top-level lifecycle, encoded as a simple transition table.
const TRANSITIONS = {
  BOOTING:         { SIGNED_IN: 'AUTHENTICATED', NEEDS_SIGN_IN: 'UNAUTHENTICATED' },
  UNAUTHENTICATED: { SIGNED_IN: 'AUTHENTICATED' },
  AUTHENTICATED:   { READY: 'READY', SIGNED_OUT: 'UNAUTHENTICATED' },
  READY:           { START_SYNC: 'SYNCING', START_BACKUP: 'BACKING_UP', START_RESTORE: 'RESTORING', SIGNED_OUT: 'UNAUTHENTICATED' },
  SYNCING:         { DONE: 'READY', FAIL: 'READY' },
  BACKING_UP:      { DONE: 'READY', FAIL: 'READY' },
  RESTORING:       { DONE: 'READY', FAIL: 'READY' },
};

function next(state, event) {
  return (TRANSITIONS[state] && TRANSITIONS[state][event]) || state;
}

let model = { state: 'BOOTING', user: null, alerts: 0, lastSyncAt: 0 };
const listeners = new Set();

function notify() {
  for (const fn of listeners) { try { fn(model); } catch (e) { console.warn('[sam] listener failed', e); } }
}

const ACTIONS = {
  signedIn:      (m, { user }) => ({ ...m, user, state: next(m.state, 'SIGNED_IN') }),
  needsSignIn:   (m)           => ({ ...m, user: null, state: next(m.state, 'NEEDS_SIGN_IN') }),
  ready:         (m)           => ({ ...m, state: next(m.state, 'READY') }),
  startSync:     (m)           => ({ ...m, state: next(m.state, 'START_SYNC') }),
  syncDone:      (m)           => ({ ...m, state: next(m.state, 'DONE'), lastSyncAt: Date.now() }),
  syncFail:      (m)           => ({ ...m, state: next(m.state, 'FAIL') }),
  startBackup:   (m)           => ({ ...m, state: next(m.state, 'START_BACKUP') }),
  backupDone:    (m)           => ({ ...m, state: next(m.state, 'DONE') }),
  startRestore:  (m)           => ({ ...m, state: next(m.state, 'START_RESTORE') }),
  restoreDone:   (m)           => ({ ...m, state: next(m.state, 'DONE') }),
  signedOut:     ()            => ({ state: 'UNAUTHENTICATED', user: null, alerts: 0, lastSyncAt: 0 }),
  alertsChanged: (m, { count })=> ({ ...m, alerts: count }),
};

/** Apply a named action. Unknown actions are no-ops. */
export function dispatch(action, payload = {}) {
  const fn = ACTIONS[action];
  if (!fn) { console.warn('[sam] unknown action', action); return model; }
  model = fn(model, payload);
  notify();
  return model;
}

/** Subscribe to model changes. Returns an unsubscribe fn. */
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getModel() { return model; }
